import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import api from "@/lib/api";
import {
  Buildings, House, Users as UsersIcon, FileText, Receipt, Money, Bell,
  ChartBar, Gear, SignOut, List, X, ClipboardText, Package, HandCoins,
  UserList, Bank, Scroll, ClipboardText as Clip, Wrench, IdentificationCard,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const NAV = {
  super_admin: [
    { to: "/", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/companies", label: "Companies", icon: Buildings },
    { to: "/users", label: "Property Managers", icon: UsersIcon },
    { to: "/reports", label: "Reports", icon: FileText },
    { to: "/audit", label: "Audit Logs", icon: Scroll },
  ],
  property_manager: [
    { to: "/", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/properties", label: "Properties", icon: House },
    { to: "/tenants", label: "Tenants", icon: IdentificationCard },
    { to: "/leases", label: "Leases", icon: Scroll },
    { to: "/landlords", label: "Landlords", icon: UserList },
    { to: "/invoices", label: "Invoices", icon: FileText },
    { to: "/payments", label: "Payments", icon: Receipt },
    { to: "/commissions", label: "Commissions", icon: HandCoins },
    { to: "/expenses", label: "Expenses", icon: Money },
    { to: "/inspections", label: "Inspections", icon: ClipboardText },
    { to: "/inventory", label: "Inventory", icon: Package },
    { to: "/reports", label: "Reports", icon: ChartBar },
    { to: "/staff", label: "Staff & Users", icon: UsersIcon },
    { to: "/settings", label: "Settings", icon: Gear },
  ],
  property_admin: [
    { to: "/", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/properties", label: "Properties", icon: House },
    { to: "/tenants", label: "Tenants", icon: IdentificationCard },
    { to: "/leases", label: "Leases", icon: Scroll },
    { to: "/landlords", label: "Landlords", icon: UserList },
    { to: "/invoices", label: "Invoices", icon: FileText },
    { to: "/payments", label: "Payments", icon: Receipt },
    { to: "/expenses", label: "Expenses", icon: Money },
    { to: "/inspections", label: "Inspections", icon: ClipboardText },
    { to: "/inventory", label: "Inventory", icon: Package },
    { to: "/reports", label: "Reports", icon: ChartBar },
  ],
  accountant: [
    { to: "/", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/invoices", label: "Invoices", icon: FileText },
    { to: "/payments", label: "Payments", icon: Receipt },
    { to: "/commissions", label: "Commissions", icon: HandCoins },
    { to: "/vouchers", label: "Vouchers", icon: Bank },
    { to: "/landlords", label: "Landlords", icon: UserList },
    { to: "/expenses", label: "Expenses", icon: Money },
    { to: "/reports", label: "Reports", icon: ChartBar },
  ],
  staff: [
    { to: "/", label: "Dashboard", icon: ChartBar, end: true },
    { to: "/properties", label: "Source Property", icon: House },
    { to: "/tenants", label: "Source Tenant", icon: IdentificationCard },
    { to: "/inspections", label: "Inspections", icon: ClipboardText },
    { to: "/inventory", label: "Inventory", icon: Package },
  ],
};

export default function Layout({ children }) {
  const { user, company, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = NAV[user?.role] || [];

  useEffect(() => {
    if (user?.company_id) {
      api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    }
  }, [user]);

  const NavList = () => (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => setOpen(false)}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-[#5C5F58] hover:bg-primary/10 hover:text-primary"
            }`
          }
        >
          <n.icon size={19} weight="duotone" />
          {n.label}
        </NavLink>
      ))}
      {user?.company_id && (
        <NavLink to="/notifications" onClick={() => setOpen(false)} data-testid="nav-notifications"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-[#5C5F58] hover:bg-primary/10 hover:text-primary"
            }`}>
          <Bell size={19} weight="duotone" />
          <span className="flex-1">Notifications</span>
          {unread > 0 && <span className="text-xs bg-[#C85A32] text-white rounded-full px-1.5 py-0.5">{unread}</span>}
        </NavLink>
      )}
    </nav>
  );

  const Brand = () => (
    <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border shrink-0">
      {company?.logo_url ? (
        <img src={company.logo_url} alt="logo" className="h-8 w-8 rounded object-cover" />
      ) : (
        <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
          <Buildings size={20} weight="fill" />
        </div>
      )}
      <div className="leading-tight">
        <div className="font-heading font-bold text-[15px] text-[#1A1C18] truncate max-w-[150px]">
          {company?.name || "Property Manager"}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">360 Work Flow</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-card border-r border-border fixed inset-y-0 z-30">
        <Brand />
        <NavList />
        <div className="p-3 border-t border-border">
          <div className="px-2 py-2 mb-2">
            <div className="text-sm font-medium text-[#1A1C18] truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
          </div>
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={logout} data-testid="logout-btn">
            <SignOut size={16} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-card border-r border-border">
            <Brand />
            <NavList />
            <div className="p-3 border-t border-border">
              <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={logout}>
                <SignOut size={16} /> Sign out
              </Button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="menu-toggle">
            <List size={24} />
          </button>
          <div className="hidden lg:block text-sm text-muted-foreground">
            {company?.name ? `${company.name} · ${company?.location || ""}` : "Property Management System"}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
              {ROLE_LABELS[user?.role]}
            </span>
            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1500px] w-full">{children}</main>
      </div>
    </div>
  );
}
